import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import axios from 'axios';

// ---------------------------------------------------------

import Button from '@components/button';
import Card from '@components/card';
import Note from '@components/notes/note';
import Spinner from '@components/spinner';
import Toast from '@components/toast';

// ---------------------------------------------------------

import styles from './styles.module.scss';
const {
  modal_container,
  listing,
  note_form,
  save_note_container,
  modal_content,
  header,
  notes_content,
  input_field,
  input_label,
  spinner,
  browser_fix
} = styles;

// ---------------------------------------------------------

const NotesCardModal = (props) => {
  const {
    image,
    setNoteChangesMade,
    setDeletingNote,
    property,
    listingId,
    canonicalUrl,
    updateNoteListings,
    modalIsOpen,
    setModalIsOpen,
    updateParent
  } = props;
  const { address, city, priceFormatted, state, zip } = property;

  const modalContent = classNames(modal_container, modal_content);
  const listing_classes = classNames(listing);
  const note_form_classes = classNames(note_form);

  const [noteInput, setNoteInput] = useState('');
  const [notes, setNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {
    if (!modalIsOpen) return;

    const getListingNotes = async (listingId) => {
      const { data, loading } = await axios.get(`/api/user/notes?listingId=${listingId}`);
      setIsLoading(loading);
      setNotes(data?.notes || []);
    };

    getListingNotes(listingId);
  }, [modalIsOpen]);

  // ---------------------------------------------------------

  const handleAddNote = async (content) => {
    const date = new Date().toISOString();
    const noteData = {
      content: content,
      createdOn: date,
      lastSavedOn: date,
      listingId: listingId,
      noteId: 0
    };

    await axios({
      data: noteData,
      method: 'POST',
      url: `/api/user/notes?listingId=${listingId}`
    })
      .then((res) => {
        setNotes([...notes, res.data]);
        setNoteInput('');
        if (updateParent) {
          setNoteChangesMade(true);
        }
        inputRef.current.focus();
        Toast.notify({
          message: 'Note saved',
          type: 'success'
        });
      })
      .catch((error) => console.error(error.message));
  };

  const handleRemoveNote = async (noteId) => {
    const newNotes = notes.filter((note) => note.noteId !== noteId);

    await axios({
      method: 'DELETE',
      url: `/api/user/notes?noteId=${noteId}`
    })
      .then(() => {
        setNotes(newNotes);
        if (updateParent) {
          setNoteChangesMade(true);
        }
        Toast.notify({
          message: 'Note deleted',
          type: 'success'
        });
      })
      .catch((error) => console.error(error.message));
  };

  const handleRemoveAllNotes = async () => {
    const body = notes.map((note) => note.noteId);

    try {
      axios.delete(`/api/user/notes`, { data: { noteIds: body } });

      setModalIsOpen(false);

      if (updateParent) {
        setDeletingNote(true);
        updateNoteListings();
      }

      Toast.notify({
        message: 'All notes deleted',
        type: 'success'
      });
    } catch (error) {
      Toast.notify({
        message: 'There was a problem deleting these notes',
        type: 'danger'
      });
    }
  };

  // ---------------------------------------------------------

  const notesItems = notes.map((note, idx) => {
    return (
      <Note
        key={note.noteId + '-' + idx}
        lastSavedOn={note.lastSavedOn}
        listingId={listingId}
        noteId={note.noteId}
        content={note.content}
        removeNote={handleRemoveNote}
        showActions
      />
    );
  });

  // ---------------------------------------------------------

  const inputRef = useRef(null);

  const handleInputChange = (e) => {
    const searchTerm = e.target.value;
    setNoteInput(searchTerm);
  };

  const getConfirmationContent = () => (
    <>
      <p>
        Are you sure you would like to remove all notes on this property? This action cannot be
        undone.
      </p>
      <div className={save_note_container}>
        <Button
          label="Yes, I'm sure. Delete all notes"
          onClick={handleRemoveAllNotes}
          theme="text-only"
        />
        <Button label="Go back" onClick={() => setShowConfirmation(false)} />
      </div>
    </>
  );

  return (
    <>
      {modalIsOpen && (
        <div className={modalContent}>
          {showConfirmation ? (
            getConfirmationContent()
          ) : (
            <>
              <div className={listing_classes}>
                <div className={header}>
                  <div className={browser_fix}>N</div>
                  <div className={browser_fix}>otes</div>
                  <div>Record your thoughts about this property...</div>
                </div>
                <Card
                  image={{
                    height: '100px',
                    src: image.src || image,
                    width: '100px'
                  }}
                  property={{
                    address: address,
                    city: city,
                    price: priceFormatted,
                    realtor: {},
                    state: state,
                    zip: zip
                  }}
                  theme="media"
                  url={canonicalUrl}
                />
              </div>
              {isLoading && (
                <div className={spinner}>
                  <Spinner />
                </div>
              )}
              <div className={notes_content}>
                {notesItems}
                <div className={note_form_classes}>
                  <label className={input_label}>
                    <span>Add note</span>
                    <textarea
                      ref={inputRef}
                      className={input_field}
                      onChange={handleInputChange}
                      placeholder={notes?.length >= 20 ? '20 note limit on a property' : ''}
                      value={noteInput}
                      name="Note"
                      required={true}
                      disabled={notes?.length >= 20}
                    />
                  </label>

                  <div className={save_note_container}>
                    <Button
                      label="Delete all notes"
                      onClick={() => setShowConfirmation(true)}
                      theme="text-only"
                    />
                    <Button
                      label="Save note"
                      onClick={() => handleAddNote(noteInput)}
                      disabled={!noteInput || notes?.length >= 20}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

NotesCardModal.propTypes = {
  canonicalUrl: PropTypes.string,

  /**
   * Specifies the image src and alt text
   */
  image: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),

  /**
   * Specifies the latest note given by the API
   */
  latestNote: PropTypes.any,

  listingId: PropTypes.any,

  listingUrl: PropTypes.string,

  /**
   * Specifies whether the modal is currently opened
   */
  modalIsOpen: PropTypes.bool,

  /**
   * Specifies the details of a property
   */
  notes: PropTypes.arrayOf(
    PropTypes.shape({
      content: PropTypes.string,
      lastSavedOn: PropTypes.string,
      listingId: PropTypes.number,
      noteId: PropTypes.number
    })
  ),

  property: PropTypes.shape({
    address: PropTypes.string,
    city: PropTypes.string,
    priceFormatted: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.string
  }),

  setDeletingNote: PropTypes.func,

  /**
   * Sets whether the modal is open or closed
   */
  setModalIsOpen: PropTypes.func,

  setNoteChangesMade: PropTypes.func,
  updateNoteListings: PropTypes.func,
  updateParent: PropTypes.bool
};

export default NotesCardModal;
